import {
  useCallback,
  useEffect,
  useMemo,
  FC,
  memo,
  useState,
  startTransition,
} from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView } from '@mui/x-tree-view';
import {
  RenderAccountTypesTree,
  AccountTypeTreeItem,
} from './AccountTypesTreeView';

export interface AccountTypesTabProps {
  initialExcludedAccountTypes?: number[];
  setFieldValue: (field: string, value: number[]) => void;
  /** When provided, "Clear all" clears every tab's exclusion filters. */
  onClearAllFilters?: () => void;
}

const AccountTypesTab: FC<AccountTypesTabProps> = memo(
  ({ initialExcludedAccountTypes = [], setFieldValue, onClearAllFilters }) => {
    const { t } = useTranslation();
    const [excludedAccountTypes, setExcludedAccountTypes] = useState<number[]>(
      initialExcludedAccountTypes,
    );

    // Sync local state with Formik values when they change (e.g., from saved filter selection)
    useEffect(() => {
      setExcludedAccountTypes(initialExcludedAccountTypes);
    }, [initialExcludedAccountTypes]);

    // Build account types structure - data is stable so this only runs once
    const accountTypes = useMemo<AccountTypeTreeItem[]>(
      () => [
        {
          id: 'type-1',
          name: t('linkedAccounts'),
          description: t('linkedAccountsDescription'),
          code: 1,
        },
        {
          id: 'type-2',
          name: t('manuallyEnteredAccounts'),
          description: t('manuallyEnteredAccountsDescription'),
          code: 2,
        },
        {
          id: 'type-8',
          name: t('managedByAdvisor'),
          description: t('managedByAdvisorDescription'),
          code: 8,
        },
      ],
      [t],
    );

    // Handle account type checkbox toggle - updates local state and global state
    // Checkbox selections don't affect data loading
    const handleAccountTypeChange = useCallback(
      (code: number) => {
        setExcludedAccountTypes((prev) => {
          const isExcluded = prev.includes(code);
          const newExcludedAccountTypes = isExcluded
            ? prev.filter((c) => c !== code)
            : [...prev, code];

          // Update global state in a transition (non-blocking)
          startTransition(() => {
            setFieldValue('excludedAccountTypes', newExcludedAccountTypes);
          });

          return newExcludedAccountTypes;
        });
      },
      [setFieldValue],
    );

    // Render tree - re-renders when excludedAccountTypes changes to update checkbox states
    const renderedTree = useMemo(() => {
      if (!accountTypes.length) {
        return null;
      }

      return (
        <RenderAccountTypesTree
          accountTypes={accountTypes}
          initialFormikValues={{
            excludedAccountTypes,
          }}
          onAccountTypeToggle={handleAccountTypeChange}
        />
      );
    }, [accountTypes, excludedAccountTypes, handleAccountTypeChange]);

    // Show warning if no account types found
    if (accountTypes.length === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant='h6'
              color='warning.main'
            >
              ⚠
            </Typography>
            <Typography
              variant='body1'
              sx={{ ml: 1, color: 'warning.main' }}
            >
              {t('noAccountTypesFound')}
            </Typography>
          </Box>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('noAccountTypesFoundDescription')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <SimpleTreeView
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            '& .MuiTreeItem-group': {
              transition: 'none !important',
            },
            '& .MuiCollapse-root': {
              transition: 'none !important',
            },
            '& .MuiCollapse-entered': {
              transition: 'none !important',
            },
          }}
        >
          {renderedTree}
        </SimpleTreeView>

        <Box sx={{ mt: 2, textAlign: 'right', flexShrink: 0 }}>
          <Button
            size='small'
            onClick={() => {
              if (onClearAllFilters) {
                onClearAllFilters();
              } else {
                setExcludedAccountTypes([]);
                startTransition(() => {
                  setFieldValue('excludedAccountTypes', []);
                });
              }
            }}
          >
            {t('resetFilters')}
          </Button>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Re-render when setFieldValue or initial values change
    // This ensures checkboxes reflect excluded filters on mount and when Formik values change
    return (
      prevProps.setFieldValue === nextProps.setFieldValue &&
      prevProps.onClearAllFilters === nextProps.onClearAllFilters &&
      prevProps.initialExcludedAccountTypes ===
        nextProps.initialExcludedAccountTypes
    );
  },
);
AccountTypesTab.displayName = 'AccountTypesTab';

export default AccountTypesTab;
